import { useMemo } from "react";

import type { Event, EventContext } from "types/events";

import { useSite } from "~/hooks/useSite";

/**
 * Safely push events to Google Tag Manager (window.dataLayer).
 * Google Tag Manager is included as an ordinary script via gatsby-ssr.tsx.
 * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events
 */
export function useGoogleTagManager() {
  const site = useSite();

  const eventContext = useMemo<EventContext>(
    () => ({
      site_handle: site.handle,
      currency: site.currencyCode,
    }),
    [site],
  );

  return useMemo(
    () => ({
      push: (event: Event) => {
        if ("undefined" === typeof window) return;

        const dataLayer = window.dataLayer;
        if (!dataLayer) return;

        // Extract properties from event.
        const { event: eventName, properties } = event;

        // Determine if this event is a GTM "ecommerce" event.
        const isEcommerceEvent = [
          "view_item",
          "view_item_list",
          "select_item",
          "add_to_cart",
          "remove_from_cart",
          "view_cart",
          "add_to_wishlist",
          "remove_from_wishlist",
          "select_promotion",
          "login",
          "sign_up",
        ].includes(eventName);

        /**
         * For GTM "ecommerce" events, nest the properties into an "ecommerce" key.
         * Otherwise, flatten the properties on to a simple object.
         */
        const payload = isEcommerceEvent
          ? {
              event: eventName,
              ecommerce: {
                ...eventContext,
                ...properties,
              },
            }
          : { event: eventName, ...eventContext, ...properties };

        /**
         * If this payload contains an ecommerce object, clear the previous one first.
         * Without this, Google Tag Manager will merge the new object with the old object.
         */
        if (payload.ecommerce) {
          dataLayer.push({ ecommerce: null });
        }

        dataLayer.push(payload);
      },
    }),
    [eventContext],
  );
}

declare global {
  interface Window {
    dataLayer?: {
      push: (payload: Record<string, any>) => void;
    };
  }
}
